import { useQuery } from '@tanstack/react-query';

import { SearchQuerySchema, type Ticker, type WindowKey } from '@meridian/contracts';
import { SEARCH_LIMIT_DEFAULT } from '@meridian/core';

import { stockKeys } from '../query-keys/stock';
import { useRepository } from '../repository-context';

/** Position of `ticker` in `stockKeys.{series,peers,diversifiers}`. */
const TICKER_KEY_INDEX = 3;

/**
 * Window-keyed panels hold the previous payload on screen while the next
 * window loads — the shape is identical, so swapping in a skeleton buys
 * nothing and costs a layout shift (the live chart is a 300px SVG inside
 * padding, the skeleton a bare 288px block). A TICKER change is not the same
 * case: it would render the previous company's numbers under the new hero
 * header, so the carry-over stops at the ticker boundary. `keepPreviousData`
 * cannot draw that distinction — to it both are simply a key change.
 */
function isSameTicker(
  previousQuery: { readonly queryKey: readonly unknown[] } | undefined,
  ticker: Ticker,
): boolean {
  return previousQuery?.queryKey[TICKER_KEY_INDEX] === ticker;
}

export function useSecurityQuery(ticker: Ticker) {
  const repo = useRepository();
  return useQuery({
    queryKey: stockKeys.security(ticker),
    queryFn: () => repo.getSecurity(ticker),
  });
}

export function useSeriesQuery(ticker: Ticker, window: WindowKey) {
  const repo = useRepository();
  return useQuery({
    queryKey: stockKeys.series(ticker, window),
    queryFn: () => repo.getSeries(ticker, { window }),
    placeholderData: (previous, previousQuery) =>
      isSameTicker(previousQuery, ticker) ? previous : undefined,
  });
}

export function usePeersQuery(ticker: Ticker, window: WindowKey) {
  const repo = useRepository();
  return useQuery({
    queryKey: stockKeys.peers(ticker, window),
    queryFn: () => repo.getPeers(ticker, { window }),
    placeholderData: (previous, previousQuery) =>
      isSameTicker(previousQuery, ticker) ? previous : undefined,
  });
}

export function useDiversifiersQuery(ticker: Ticker, window: WindowKey) {
  const repo = useRepository();
  return useQuery({
    queryKey: stockKeys.diversifiers(ticker, window),
    queryFn: () => repo.getDiversifiers(ticker, { window }),
    placeholderData: (previous, previousQuery) =>
      isSameTicker(previousQuery, ticker) ? previous : undefined,
  });
}

export function useSearchQuery(q: string, limit: number = SEARCH_LIMIT_DEFAULT) {
  const repo = useRepository();
  const parsed = SearchQuerySchema.safeParse({ q, limit });
  const enabled = parsed.success && parsed.data.q.trim().length > 0;
  return useQuery({
    queryKey: stockKeys.search(parsed.success ? parsed.data.q : q, limit),
    queryFn: () => {
      const body = SearchQuerySchema.parse({ q, limit });
      return repo.search(body);
    },
    enabled,
    staleTime: 30_000,
  });
}
